import apiClient from '../api';

class AuditService {
    constructor() {
        this.sessionId = this.generateSessionId();
        this.pageStartTime = Date.now();
        this.isTracking = true;
        this.activityQueue = [];
        this.batchSize = 10;
        this.flushInterval = 5000; // 5 seconds
        
        this.init();
    }
    
    generateSessionId() {
        return `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }
    
    init() {
        if (typeof window === 'undefined') return;
        
        // Set session ID in header for backend tracking
        if (apiClient.defaults.headers.common) {
            apiClient.defaults.headers.common['x-session-id'] = this.sessionId;
        }
        
        // Track page visits
        this.trackPageVisit();
        
        // Track page unload for duration calculation
        window.addEventListener('beforeunload', () => {
            this.trackPageLeave();
        });
        
        // Track visibility changes
        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                this.trackPageLeave();
            } else {
                this.trackPageVisit();
            }
        });
        
        // Start periodic flushing
        this.startPeriodicFlush();
        
        // Track performance metrics
        this.trackPerformanceMetrics();
    }
    
    trackPageVisit() {
        this.pageStartTime = Date.now();
        const resource = this.getResourceFromPath(window.location.pathname);
        
        this.logActivity({
            action: 'page_visit',
            resource,
            endpoint: window.location.pathname,
            performanceMetrics: this.getPerformanceMetrics()
        });
    }
    
    trackPageLeave() {
        if (this.pageStartTime) {
            const duration = Date.now() - this.pageStartTime;
            const resource = this.getResourceFromPath(window.location.pathname);
            
            this.logActivity({
                action: 'navigation',
                resource,
                endpoint: window.location.pathname,
                details: {
                    duration,
                    event: 'page_leave'
                }
            });
            
            // Flush immediately on page leave
            this.flush();
        }
    }
    
    trackButtonClick(buttonLabel, additionalDetails = {}) {
        const resource = this.getResourceFromPath(window.location.pathname);
        
        this.logActivity({
            action: 'button_click',
            resource,
            endpoint: window.location.pathname,
            details: {
                buttonLabel,
                ...additionalDetails
            }
        });
    }
    
    trackFormSubmit(formType, formData = {}, isSuccess = true) {
        const resource = this.getResourceFromPath(window.location.pathname);
        
        // Sanitize form data - remove sensitive fields
        const sanitizedData = { ...formData };
        const sensitiveFields = ['password', 'currentPassword', 'newPassword', 'token'];
        sensitiveFields.forEach(field => delete sanitizedData[field]);
        
        this.logActivity({
            action: 'form_submit',
            resource,
            endpoint: window.location.pathname,
            details: {
                formType,
                isSuccess,
                formData: sanitizedData
            }
        });
    }
    
    trackSearch(searchTerm, results = 0) {
        const resource = this.getResourceFromPath(window.location.pathname);
        
        this.logActivity({
            action: 'search',
            resource,
            endpoint: window.location.pathname,
            details: {
                searchTerm,
                resultCount: results
            }
        });
    }
    
    trackFilter(filterCriteria) {
        const resource = this.getResourceFromPath(window.location.pathname);
        
        this.logActivity({
            action: 'filter',
            resource,
            endpoint: window.location.pathname,
            details: {
                filterCriteria
            }
        });
    }
    
    trackDownload(fileName, fileType) {
        const resource = this.getResourceFromPath(window.location.pathname);
        
        this.logActivity({
            action: 'download',
            resource,
            endpoint: window.location.pathname,
            details: {
                fileName,
                fileType
            }
        });
    }
    
    trackError(error, context = {}) {
        const resource = this.getResourceFromPath(window.location.pathname);
        
        this.logActivity({
            action: 'api_call',
            resource,
            endpoint: window.location.pathname,
            details: {
                errorMessage: error.message || error,
                errorContext: context,
                isError: true
            }
        });
    }
    
    // Internal helper methods
    getResourceFromPath(path) {
        const pathSegments = path.split('/').filter(Boolean);
        if (pathSegments.length === 0) return 'dashboard';
        
        const resourceMap = {
            'customers': 'customers',
            'trucks': 'trucks',
            'forms': 'forms',
            'admin': 'admin',
            'safety': 'safety',
            'dashboard': 'dashboard',
            'truck-detail': 'trucks',
            'reset-password': 'authentication',
            'forgot-password': 'authentication'
        };
        
        return resourceMap[pathSegments[0]] || pathSegments[0];
    }
    
    getPerformanceMetrics() {
        if (typeof window === 'undefined' || !window.performance) return {};
        
        const navigation = performance.getEntriesByType('navigation')[0];
        const memory = performance.memory;
        
        return {
            pageLoadTime: navigation ? navigation.loadEventEnd - navigation.fetchStart : 0,
            domContentLoaded: navigation ? navigation.domContentLoadedEventEnd - navigation.fetchStart : 0,
            timeToFirstByte: navigation ? navigation.responseStart - navigation.fetchStart : 0,
            memoryUsage: memory ? memory.usedJSHeapSize : 0
        };
    }
    
    trackPerformanceMetrics() {
        // Track core web vitals if available
        if (typeof window !== 'undefined' && 'PerformanceObserver' in window) {
            // Largest Contentful Paint
            new PerformanceObserver((entryList) => {
                const entries = entryList.getEntries();
                const lastEntry = entries[entries.length - 1];
                this.logActivity({
                    action: 'page_visit',
                    resource: this.getResourceFromPath(window.location.pathname),
                    endpoint: window.location.pathname,
                    performanceMetrics: {
                        largestContentfulPaint: lastEntry.startTime
                    }
                });
            }).observe({ entryTypes: ['largest-contentful-paint'] });
            
            // First Input Delay
            new PerformanceObserver((entryList) => {
                const entries = entryList.getEntries();
                entries.forEach(entry => {
                    this.logActivity({
                        action: 'page_visit',
                        resource: this.getResourceFromPath(window.location.pathname),
                        endpoint: window.location.pathname,
                        performanceMetrics: {
                            firstInputDelay: entry.processingStart - entry.startTime
                        }
                    });
                });
            }).observe({ entryTypes: ['first-input'] });
        }
    }
    
    logActivity(activityData) {
        if (!this.isTracking) return;
        
        // Only log activity if user is authenticated
        const token = localStorage.getItem('authToken');
        if (!token) {
            return; // Skip logging for unauthenticated users
        }
        
        const activity = {
            ...activityData,
            timestamp: new Date().toISOString(),
            sessionId: this.sessionId
        };
        
        this.activityQueue.push(activity);
        
        // Auto-flush if queue is full
        if (this.activityQueue.length >= this.batchSize) {
            this.flush();
        }
    }
    
    async flush() {
        if (this.activityQueue.length === 0) return;
        
        // Only send audit logs if user is authenticated
        const token = localStorage.getItem('authToken');
        if (!token) {
            console.log('[AuditService] Skipping activity log - user not authenticated');
            this.activityQueue = []; // Clear queue for unauthenticated users
            return;
        }
        
        const activities = [...this.activityQueue];
        this.activityQueue = [];
        
        try {
            // Send all activities in batch
            await Promise.all(
                activities.map(activity => 
                    apiClient.post('/audit/activity', activity)
                        .catch(err => console.warn('Failed to log activity:', err))
                )
            );
        } catch (error) {
            console.warn('Error flushing activity logs:', error);
            // Re-queue failed activities (but limit to prevent infinite growth)
            if (this.activityQueue.length < 50) {
                this.activityQueue.unshift(...activities);
            }
        }
    }
    
    startPeriodicFlush() {
        setInterval(() => {
            this.flush();
        }, this.flushInterval);
    }
    
    // Public API methods for manual tracking
    enable() {
        this.isTracking = true;
    }
    
    disable() {
        this.isTracking = false;
    }
    
    setUser(user) {
        this.userId = user?.id || user?._id;
    }
}

// Audit Log API functions
export const auditAPI = {
    // Get audit logs with filtering
    getLogs: async (params = {}) => {
        const response = await apiClient.get('/audit/logs', { params });
        return response.data;
    },
    
    // Get audit summary/analytics
    getSummary: async (timeframe = '24h') => {
        const response = await apiClient.get('/audit/summary', { 
            params: { timeframe } 
        });
        return response.data;
    },
    
    // Get user activity timeline
    getUserActivity: async (userId, params = {}) => {
        const response = await apiClient.get(`/audit/user/${userId}`, { params });
        return response.data;
    },
    
    // Export audit logs
    exportLogs: async (params = {}) => {
        const response = await apiClient.get('/audit/export', { 
            params,
            responseType: 'blob' 
        });
        return response;
    },
    
    // Get real-time activity
    getRealTimeActivity: async () => {
        const response = await apiClient.get('/audit/realtime');
        return response.data;
    },

    getUsage: async (params = {}) => {
        const response = await apiClient.get('/audit/usage', { params });
        return response.data;
    }
};

// Create singleton instance
const auditService = new AuditService();

// Enhanced tracking functions for easy use throughout the app
export const trackButtonClick = (buttonLabel, details) => 
    auditService.trackButtonClick(buttonLabel, details);

export const trackFormSubmit = (formType, formData, isSuccess) => 
    auditService.trackFormSubmit(formType, formData, isSuccess);

export const trackSearch = (searchTerm, results) => 
    auditService.trackSearch(searchTerm, results);

export const trackFilter = (filterCriteria) => 
    auditService.trackFilter(filterCriteria);

export const trackDownload = (fileName, fileType) => 
    auditService.trackDownload(fileName, fileType);

export const trackError = (error, context) => 
    auditService.trackError(error, context);

export default auditService; 